// Assets (spec/dashboard.md sections 9.7–9.9): the tabs Files · Addressables · Scheduling (route-bound,
// `branches/:branch/assets/:tab?`, Files by default).
import { useParams } from 'react-router';
import { useContent } from '../../content/ContentContext';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { EmptyState } from '../../ui/Banner';
import { CalendarDays } from '../../ui/icons';
import { Tabs } from '../../ui/Tabs';
import { DocumentsPage } from '../DocumentsPage';
import { AddressablesTab } from './AddressablesTab';
import { FilesTab } from './FilesTab';
import './assets.css';

const SCHEDULING_TEMPLATE = 'SmartObjects.AssetsLifeTime';
type Tab = 'files' | 'addressables' | 'scheduling';

export function AssetsPage() {
  const { org, game } = useGame();
  const { branch = '', tab: rawTab } = useParams();
  const tab: Tab = rawTab === 'addressables' || rawTab === 'scheduling' ? rawTab : 'files';
  const catalog = useContent();
  const at = (t: Tab) => routes.branch(org, game.slug, branch, `assets/${t}`);
  return (
    <section className="assets-page">
      <div className="page-head">
        <h1>Assets</h1>
        {!catalog.loading && (
          <span className="page-meta">
            {catalog.assetList.length} asset{catalog.assetList.length === 1 ? '' : 's'}
          </span>
        )}
      </div>
      <Tabs
        ariaLabel="Assets"
        items={[
          { key: 'files', label: 'Files', to: at('files') },
          { key: 'addressables', label: 'Addressables', to: at('addressables') },
          { key: 'scheduling', label: 'Scheduling', to: at('scheduling') },
        ]}
      />
      <div className="assets-tab">
        {tab === 'files' && <FilesTab />}
        {tab === 'addressables' && <AddressablesTab />}
        {tab === 'scheduling' && <SchedulingTab />}
      </div>
    </section>
  );
}

/** Section 9.9: the documents grid of `SmartObjects.AssetsLifeTime` when the branch has the template, else where it comes from. */
function SchedulingTab() {
  const catalog = useContent();
  if (catalog.templates.has(SCHEDULING_TEMPLATE)) return <DocumentsPage template={SCHEDULING_TEMPLATE} title="Scheduling" />;
  if (catalog.loading)
    return (
      <div className="pane">
        <div className="skeleton-rows" role="status" aria-label="Loading">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skeleton-row">
              <span className="skeleton" />
              <span className="skeleton" />
            </div>
          ))}
        </div>
      </div>
    );
  return (
    <EmptyState
      icon={<CalendarDays />}
      title="No asset scheduling on this branch"
      body={
        <>
          Scheduling edits the documents of the template <code>{SCHEDULING_TEMPLATE}</code> — the LiveOps package's asset lifetime documents: the preload and keep-alive conditions and the content list. The template comes with the LiveOps package's <code>SmartObjects</code> templates, which a branch imported from a project using the package carries; this branch has no such template, so there is nothing to schedule here.
        </>
      }
    />
  );
}
